<template>
    <div class="dialog" id="params-dialog" aria-hidden="true">
        <div class="dialog-overlay" tabindex="-1" data-a11y-dialog-hide></div>
        <div
            role="dialog"
            class="dialog-content dialog-content--furniture"
            aria-labelledby="my-dialog-title"
            aria-describedby="my-dialog-description"
        >
            <button
                data-a11y-dialog-hide
                class="dialog-close"
                :aria-label="$t('project_details.proposal.parameters_modal.dialog_close')"
            >
                <img src="@/assets/images/dialog-close.svg" :alt="$t('project_details.proposal.parameters_modal.dialog_close')" />
            </button>

            <div class="project-add">
                <div class="params__heading">{{ $t('project_details.proposal.parameters_modal.furniture_params') }}</div>
                <div class="options">
                    <div class="options__name">{{ $t('project_details.proposal.parameters_modal.rooms') }}</div>
                    <button class="project-add__option"
                            v-for="(room, roomIndex) in rooms"
                            @click.prevent="changeActiveRoom(roomIndex)"
                            :class="{
                                'project-add__option--active': activeRoomIndex == roomIndex
                            }"
                    >
                        {{ room?.room?.name }}
                    </button>
                </div>
                <div class="options" v-if="rooms && rooms.length > 0">
                    <div class="options__name">Produkt</div>
                    <button class="project-add__option"
                            v-for="(parameter, parameterSlug) in rooms[activeRoomIndex].parameters"
                            @click.prevent="displayRoomFurnitureParameters(activeRoomIndex, parameterSlug)"
                            :class="{
                                'project-add__option--active': activeParameter === parameterSlug
                            }"
                    >{{ parameter.furniture_name }}</button>
                </div>
                <div class="params__inner" v-if="rooms && rooms.length > 0">
                    <div class="params__column"
                         v-for="activeParameterColumnDetails in activeParameterDetailsArray"
                    >
                        <template v-for="leftSideParameter in activeParameterColumnDetails">
                            <template v-if="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.type === 'boolean'">
                                <div class="params__section">
                                    <p>{{rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.name}}</p>
                                    <div class="checkbox" v-for="(checkboxParam, index) in rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.values">
                                        <input type="checkbox" :id="index" v-model="rooms[activeRoomIndex].parameters[activeParameter].furniture_parameters[leftSideParameter].values[index].isSelected" />
                                        <label :for="index">{{ checkboxParam?.name }}</label>
                                    </div>
                                </div>
                            </template>
                            <template v-if="rooms[activeRoomIndex].parameters[activeParameter].furniture_parameters[leftSideParameter]?.type === 'value'">
                                <div class="params__section range-slider">
                                    <p v-if="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.unit">{{rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.name}} ({{rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.unit}})</p>
                                    <p v-else>{{rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.name}}</p>
                                    <div class="inner">
                                        <span>{{ $t('project_details.proposal.parameters_modal.from') }}:</span>
                                        <input type="number"
                                               class="inputLower"
                                               :min="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.min"
                                               :max="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.max"
                                               v-model="rooms[activeRoomIndex].parameters[activeParameter].furniture_parameters[leftSideParameter].selected_min_value"/>
                                        <span>{{ $t('project_details.proposal.parameters_modal.to') }}:</span>
                                        <input
                                            type="number"
                                            class="inputUpper"
                                            :min="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.min"
                                            :max="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.max"
                                            v-model="rooms[activeRoomIndex].parameters[activeParameter].furniture_parameters[leftSideParameter].selected_max_value" />
                                    </div>
                                    <span class="multi-range">
                                        <input
                                            type="range"
                                            class="rangeLower"
                                            :min="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.min"
                                            :max="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.max"
                                            v-model="rooms[activeRoomIndex].parameters[activeParameter].furniture_parameters[leftSideParameter].selected_min_value"
                                        />
                                        <input
                                            type="range"
                                            class="rangeUpper"
                                            :min="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.min"
                                            :max="rooms[activeRoomIndex].parameters[activeParameter]?.furniture_parameters[leftSideParameter]?.max"
                                            v-model="rooms[activeRoomIndex].parameters[activeParameter].furniture_parameters[leftSideParameter].selected_max_value"
                                        />
                                    </span>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
                <button
                    class="select"
                    @click.prevent="submitParameters"
                    :class="{'disabled': sendingForm}"
                    :disabled="sendingForm"
                >
                    <template v-if="!sendingForm">
                        {{ $t('project_details.proposal.parameters_modal.save') }}
                    </template>
                    <template v-else>
                        {{ $t('project_details.proposal.parameters_modal.saving') }}
                    </template>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {inject, onMounted, ref} from "vue";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import router from "@/router";
import authHelper from "@/helpsers/auth.helper";
import AlertHelper from "@/helpsers/alert.helper";
import {useI18n} from "vue-i18n";

export default {
    name: "DialogParamsModal",
    setup() {
        const sendingForm = ref(false);
        const paramsDialog = ref();
        let alert = inject('alert');
        const {t} = useI18n();
        const rooms = ref();
        let activeRoomIndex = ref('');
        let activeParameter = ref('');
        let activeParameterDetailsArray = ref([]);

        onMounted(() => {
            paramsDialog.value = new A11yDialog(document.querySelector("#params-dialog"));
        })

        function changeActiveRoom(roomIndex) {
            if (activeRoomIndex.value != roomIndex) {
                activeParameterDetailsArray.value = [];
                activeParameter.value = '';
                activeRoomIndex.value = roomIndex

                for (let furnitureSlug in rooms.value[roomIndex]?.parameters) {
                    displayRoomFurnitureParameters(roomIndex, furnitureSlug)
                    break;
                }
            }
        }

        function showModal(roomsData) {
            if (roomsData) {
                rooms.value = roomsData;
                paramsDialog.value.show();

                for (let roomIndex in rooms.value) {
                    activeRoomIndex.value = roomIndex

                    for (let furnitureSlug in rooms.value[roomIndex]?.parameters) {
                        displayRoomFurnitureParameters(roomIndex, furnitureSlug)
                        break;
                    }

                    if (activeRoomIndex.value)
                        break;
                }
            }
        }

        function displayRoomFurnitureParameters(roomIndex, furnitureSlug) {
            if (activeParameter.value !== furnitureSlug) {
                activeParameterDetailsArray.value = {
                    'left': [],
                    'right': [],
                };
                activeParameter.value = furnitureSlug;
                if (rooms.value[roomIndex]?.parameters[furnitureSlug]?.furniture_parameters) {
                    let loopIndex = 0;
                    for (let furnitureParameterSlug in rooms.value[roomIndex]?.parameters[furnitureSlug].furniture_parameters) {
                        if (loopIndex % 2 === 0) {
                            if (rooms.value[roomIndex]?.parameters[furnitureSlug].furniture_parameters[furnitureParameterSlug]?.type === 'value')
                                activeParameterDetailsArray.value['left'].unshift(furnitureParameterSlug);
                            else
                                activeParameterDetailsArray.value['left'].push(furnitureParameterSlug);
                        } else {
                            if (rooms.value[roomIndex]?.parameters[furnitureSlug].furniture_parameters[furnitureParameterSlug]?.type === 'value')
                                activeParameterDetailsArray.value['right'].unshift(furnitureParameterSlug);
                            else
                                activeParameterDetailsArray.value['right'].push(furnitureParameterSlug);
                        }
                        loopIndex++;
                    }
                }
            }
        }

        function mapParametersForRequest() {
            let resultParameters = [];
            let roomParameters = {};

            for (let roomIndex in rooms.value) {

                roomParameters = {};

                for (let furnitureIndex in rooms.value[roomIndex].parameters) {
                    roomParameters[furnitureIndex] = {'furniture_parameters': {}};
                    for (let furnitureParameterIndex in rooms.value[roomIndex].parameters[furnitureIndex].furniture_parameters) {
                        if (rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.type === 'boolean') {
                            let furnitureParameterValues = {}
                            for (let furnitureParameterValueIndex in rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.values) {
                                furnitureParameterValues[furnitureParameterValueIndex] = rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.values[furnitureParameterValueIndex]?.isSelected
                            }
                            roomParameters[furnitureIndex].furniture_parameters[furnitureParameterIndex] = {
                                type: rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.type,
                                values: furnitureParameterValues
                            }
                        } else if (rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.type === 'value') {
                            roomParameters[furnitureIndex].furniture_parameters[furnitureParameterIndex] = {
                                type: rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.type,
                                selected_min_value: parseInt(rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.selected_min_value),
                                selected_max_value: parseInt(rooms.value[roomIndex].parameters[furnitureIndex]?.furniture_parameters[furnitureParameterIndex]?.selected_max_value),
                            }
                        }
                    }
                }


                resultParameters.push({
                    roomId: rooms.value[roomIndex].id,
                    roomParameters: roomParameters,
                })

            }

            return resultParameters;
        }

        const submitParameters = async () => {
            sendingForm.value = true;
            let resultParameters = mapParametersForRequest();
            axios.put(ApiHelper.updateManyRoomsParametersUrl(router.currentRoute.value.params.id),
                {
                    parameters: resultParameters,
                },
                {
                    headers: authHelper.authHeader()
                }
            ).then(response => {
                sendingForm.value = false;
                // emitter.emit('refreshProjectDetails');//todo
                paramsDialog.value.hide();
                AlertHelper.show(alert, t('project_details.proposal.parameters_modal.alert_success'));
            }).catch(error => {
                sendingForm.value = false;
                let errorMessage = ApiHelper.getResponseMessage(error.response?.data?.code);
                AlertHelper.show(alert, errorMessage, '', 'error');
            });
        }

        return {
            showModal,
            rooms,
            activeRoomIndex,
            changeActiveRoom,
            sendingForm,
            displayRoomFurnitureParameters,
            activeParameter,
            activeParameterDetailsArray,
            submitParameters
        }
    }
}
</script>

<style scoped lang="scss">
@use "@/css/proposals";

.dialog-content--furniture {
    width: 700px;
}

#params-dialog {
    .options {
        overflow: auto;

        &__name {
            padding: 20px 0;
        }
    }

    .project-add {
        .select {
            width: auto;
        }
    }
}
</style>